import React, { MouseEvent, ReactElement } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  ProductCard,
  ProductCardProps,
  Variant,
} from '@bucketplace/common';
import { MEDIA_QUERY } from '@bucketplace/design-system';
import { Button } from '@bucketplace/design-system/bds';
import { sendDeepLinkAction } from '@bucketplace/owap-sdk';

import { usePlatformInfo } from '@/builder/hooks';
import { useOwapInfo } from '@/utils';

import { TextBadge, TextBadgeProps } from './TextBadge';

export type ExhibitionProductCardProps = ProductCardProps & TextBadgeProps & {
  useBuyButton?: boolean,
  variant?: Variant,
};

export function ExhibitionProductCard({
  product,
  textBadgeList,
  href,
  logData,
  isMdPick,
  variant = 'lg',
  useBuyButton,
  badgeType,
  flex,
  linkComponent,
}: ExhibitionProductCardProps): ReactElement {
  const {
    isSelling,
    isDiscontinued,
  } = product;
  const { isInApp } = usePlatformInfo();
  const { isOwap } = useOwapInfo();
  const isOwapApp = isInApp && isOwap;

  // 판매중이면서 단종이 아닐 경우에 뱃지 노출
  const showBadge = isSelling && !isDiscontinued;
  // 배너일 경우 반 사이즈 정도 카드를 가리기 때문에 가린만큼 padding을 줌
  const hasBanner = badgeType === 'banner' && textBadgeList.some(({ type, value }) => type === 'banner' && value !== '');
  const renderAdditionalInfo = (): ReactElement | undefined => {
    switch (true) {
      case useBuyButton:
        return (
          <BuyButtonWrapper flex={flex}>
            <Button variant="primary1" size={32} full>
              구매하기
            </Button>
          </BuyButtonWrapper>
        );
      default:
    }
  };
  const handleProductDeepLinkAction = (event: MouseEvent): void => {
    if (!isOwapApp) {
      return;
    }

    event.preventDefault();
    sendDeepLinkAction('move', {
      type: 'fullPage',
      url: href,
      isNative: true,
      isAllowOutboundLink: false,
    });
  };

  return (
    <StyledProductCard
      product={{
        ...product,
        ...product.couponBadge && {
          couponBadge: {
            ...product.couponBadge,
            couponAppliedPrice: undefined,
          },
        },
      }}
      href={href}
      isMdPick={isMdPick}
      flex={flex}
      linkComponent={linkComponent}
      showReview={false}
      showBadge={showBadge && !useBuyButton}
      renderOverlay={() => (
        <TextBadge
          badgeType={badgeType}
          textBadgeList={[...textBadgeList]}
          flex={flex}
        />
      )}
      logData={logData}
      hasBanner={hasBanner}
      infoBottomArea={renderAdditionalInfo()}
      infoVariant={variant}
      onClick={(event) => handleProductDeepLinkAction(event)}
    />
  );
}

const BuyButtonWrapper = styled.div<{ flex?: boolean }>`
  display: flex;
  align-items: flex-end;
  align-self: end;
  width: 100%;
  height: 100%;
  ${({ flex }) => (flex
    ? css`
    max-width: 110px;
    margin-top: 22px;
    float: right;
  `
    : css`
    margin: 16px 0 20px 0;
    ${MEDIA_QUERY.max('tablet', css`
      margin: 16px 0 0 0;
    `)}
  `)}
`;

const StyledProductCard = styled(ProductCard)<{ hasBanner: boolean, flex?: boolean }>`
  border-bottom: none;
  ${({ flex }) => flex && css`
    height: 100%;
  `}
  padding: 0px;

  .product-info {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-self: start;
  }

  ${({ hasBanner }) => hasBanner && css`
    padding-top: 10px !important;
  `}
`;
